import {
    Alert, Avatar, Box, Chip, CircularProgress, FormControl, IconButton,
    MenuItem, Paper, Select, SelectChangeEvent, Tab, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, Tabs, Tooltip, Typography
} from "@mui/material"
import { LoadingButton } from "@mui/lab"
import { AccountBalanceOutlined, PhoneIphoneOutlined, PlayCircleOutline, Refresh } from "@mui/icons-material"
import { FC, useEffect, useState } from "react"
import PageTitle from "../Common/PageTitle"
import NoData from "../Common/NoData"
import ConfirmPinDialog from "../Common/ConfirmPinDialog"
import { Apis, numberWithCommas } from "../../helpers"
import { TABLE_HEIGHT } from "../../configs"
import { EventsEmit } from "../../configs/events"

interface WithdrawalRow {
    _id: string
    status: 'Pending' | 'Processing' | 'Completed'
    purpose: string
    initiatedAt: string
    phone?: string
    bankAccount?: string
    amount: number
    method: 'mpesa' | 'bank'
    recipientName: string
    recipientLogo?: any
}

type StatusFilter = 'pending_processing' | 'Pending' | 'Processing' | 'Completed' | 'all'
type MethodFilter = 'all' | 'bank' | 'mpesa'

interface StateProps {
    withdrawals: WithdrawalRow[]
    loading: boolean
    refreshing: boolean
    status: StatusFilter
    method: MethodFilter
    page: number
    pageSize: number
    count: number
}

const STATUS_STYLE: Record<string, { color: string; border: string }> = {
    Pending: { color: '#e65100', border: '#e65100' },
    Processing: { color: '#6a1b9a', border: '#6a1b9a' },
    Completed: { color: '#2e7d32', border: '#2e7d32' },
    Failed: { color: '#c62828', border: '#c62828' },
}

const statusParam = (filter: StatusFilter): any =>
    filter === 'pending_processing' ? ['Pending', 'Processing'] :
        filter === 'all' ? 'all' : filter

// ── Referral payouts ──────────────────────────────────────────────────────

interface ReferralWithdrawalRow {
    _id: string
    status: 'Pending' | 'Processing' | 'Completed' | 'Failed'
    period: string
    grossAmount: number
    fee: number
    amount: number
    method: 'mpesa' | 'ift' | 'pesalink'
    destination: string
    MessageReference?: string
    failureReason?: string
    initiatedAt: string
    completedAt?: string
    userName?: string
    userPhone?: string
    userEmail?: string
}

interface ProcessSummary {
    period: string
    eligible: number
    processed: number
    failed: number
    pending: number
    skippedInactive: number
    skippedInsufficientBalance: number
    alreadyProcessed: number
    errors: string[]
}

type ReferralStatusFilter = 'all' | 'Pending' | 'Processing' | 'Completed' | 'Failed'

const METHOD_LABEL: Record<string, string> = {
    mpesa: 'Mpesa', ift: 'Bank Transfer', pesalink: 'PesaLink'
}

interface ReferralStateProps {
    withdrawals: ReferralWithdrawalRow[]
    loading: boolean
    refreshing: boolean
    status: ReferralStatusFilter
    page: number
    pageSize: number
    count: number
    confirmingProcess: boolean
    processing: boolean
    pinError: string | null
    lastSummary: ProcessSummary | null
}

const SummaryStat: FC<{ label: string, value: number, color: string }> = ({ label, value, color }) => (
    <Box sx={{ minWidth: 120 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color }}>{value}</Typography>
        <Typography sx={{ fontSize: 11.5, color: '#667085' }}>{label}</Typography>
    </Box>
)

const ReferralPayouts: FC = () => {
    const [state, setState] = useState<ReferralStateProps>({
        withdrawals: [],
        loading: true,
        refreshing: false,
        status: 'all',
        page: 0,
        pageSize: 25,
        count: 0,
        confirmingProcess: false,
        processing: false,
        pinError: null,
        lastSummary: null
    })
    const {
        withdrawals, loading, refreshing, status, page, pageSize, count,
        confirmingProcess, processing, pinError, lastSummary
    } = state

    const fetchWithdrawals = ({ newStatus, newPage, newPageSize, processVariable }: any = {}) => {
        const Status = newStatus || state.status
        const Page = newPage != null && newPage > -1 ? newPage : state.page
        const PageSize = newPageSize || state.pageSize
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const afterLoad = { loading: false, refreshing: false, status: Status, page: Page, pageSize: PageSize }
        Apis({ module: 'Banks', action: 'getReferralWithdrawals', status: Status === 'all' ? undefined : Status, page: Page, pageSize: PageSize })
            .then((resp: any) => setState(s => ({ ...s, withdrawals: resp.withdrawals || [], count: resp.count ?? s.count, ...afterLoad })))
            .catch(() => setState(s => ({ ...s, ...afterLoad })))
    }

    useEffect(() => {
        fetchWithdrawals({ processVariable: 'loading' })
        // eslint-disable-next-line
    }, [])

    const runProcessWithdrawals = (pin: string) => {
        setState(s => ({ ...s, processing: true, pinError: null }))
        Apis({ module: 'Banks', action: 'processReferralWithdrawals', pin })
            .then((resp: any) => {
                if (resp?.error) {
                    setState(s => ({ ...s, processing: false, pinError: resp.message || 'Wrong PIN.' }))
                    return
                }
                setState(s => ({ ...s, processing: false, confirmingProcess: false, pinError: null, lastSummary: resp }))
                EventsEmit('app', { info: { message: `Processed ${resp.eligible} eligible referral(s) for ${resp.period}.`, status: 'success' } })
                fetchWithdrawals({ processVariable: 'refreshing' })
            })
            .catch(() => setState(s => ({
                ...s, processing: false, confirmingProcess: false,
                lastSummary: null
            })))
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 3 }}>
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {count > 0 ? `${count.toLocaleString()} total` : 'Referral commission payouts'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                            value={status}
                            onChange={(e: SelectChangeEvent) => fetchWithdrawals({ newStatus: e.target.value as ReferralStatusFilter, newPage: 0, processVariable: 'loading' })}
                            sx={{ fontSize: 13, borderRadius: 2 }}
                        >
                            <MenuItem sx={{ fontSize: 13 }} value="all">All Statuses</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="Pending">Pending</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="Processing">Processing</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="Completed">Completed</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="Failed">Failed</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchWithdrawals({ processVariable: 'refreshing' })} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                    <LoadingButton
                        variant="contained"
                        size="small"
                        startIcon={<PlayCircleOutline />}
                        loading={processing}
                        disabled={processing}
                        onClick={() => setState(s => ({ ...s, confirmingProcess: true, pinError: null }))}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                        Process Withdrawals
                    </LoadingButton>
                </Box>
            </Box>

            {lastSummary && (
                <Paper variant="outlined" sx={{ borderRadius: 3, p: 2.5, mb: 3 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#667085', textTransform: 'uppercase', letterSpacing: .4, mb: 1.5 }}>
                        Last run — period {lastSummary.period}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        <SummaryStat label="Eligible" value={lastSummary.eligible} color="#101828" />
                        <SummaryStat label="Processed" value={lastSummary.processed} color="#2e7d32" />
                        <SummaryStat label="Failed" value={lastSummary.failed} color="#c62828" />
                        <SummaryStat label="Pending" value={lastSummary.pending} color="#6a1b9a" />
                        <SummaryStat label="Skipped — Inactive" value={lastSummary.skippedInactive} color="#e65100" />
                        <SummaryStat label="Skipped — Fee &gt; Balance" value={lastSummary.skippedInsufficientBalance} color="#e65100" />
                        <SummaryStat label="Already Processed" value={lastSummary.alreadyProcessed} color="#667085" />
                    </Box>
                    {lastSummary.errors?.length > 0 && (
                        <Alert severity="warning" sx={{ mt: 2, fontSize: 12.5 }}>
                            {lastSummary.errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
                            {lastSummary.errors.length > 5 && <div>and {lastSummary.errors.length - 5} more…</div>}
                        </Alert>
                    )}
                </Paper>
            )}

            {!withdrawals.length || loading ?
                <NoData message="No referral withdrawals found." loading={loading} /> :
                <Paper sx={{ width: '100%' }} elevation={0} variant="outlined">
                    <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>User</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Method</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Balance</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Fee</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Paid</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Reference</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {withdrawals.map(w => {
                                    const chipStyle = STATUS_STYLE[w.status] || STATUS_STYLE.Pending
                                    return (
                                        <TableRow hover key={w._id}>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                <Typography sx={{ fontSize: 13 }}>{w.userName?.trim() || '—'}</Typography>
                                                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{w.userPhone || w.userEmail || '—'}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                    {w.method === 'mpesa'
                                                        ? <PhoneIphoneOutlined sx={{ fontSize: 17, color: '#00838f' }} />
                                                        : <AccountBalanceOutlined sx={{ fontSize: 17, color: '#1565c0' }} />}
                                                    {METHOD_LABEL[w.method] || w.method}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>Ksh {numberWithCommas(w.grossAmount || 0)}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>Ksh {numberWithCommas(w.fee || 0)}</TableCell>
                                            <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>Ksh {numberWithCommas(w.amount || 0)}</TableCell>
                                            <TableCell sx={{ fontSize: 12 }}>{w.MessageReference || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                {w.initiatedAt
                                                    ? new Date(w.initiatedAt).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' })
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={w.status}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ fontSize: 11, color: chipStyle.color, borderColor: chipStyle.border, height: 20 }}
                                                />
                                                {w.status === 'Failed' && w.failureReason && (
                                                    <Typography sx={{ fontSize: 10.5, color: '#c62828', mt: 0.3 }}>{w.failureReason}</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={count}
                        page={page}
                        onPageChange={(_, newPage) => fetchWithdrawals({ newPage, processVariable: 'loading' })}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={e => fetchWithdrawals({ newPage: 0, newPageSize: parseInt(e.target.value, 10), processVariable: 'loading' })}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            }

            {confirmingProcess && (
                <ConfirmPinDialog
                    title="Process Referral Withdrawals"
                    message="Enter your PIN to process this month's referral commission payouts. Every active referral with a positive balance covering its transaction fee will be paid out."
                    processing={processing}
                    errorMessage={pinError}
                    onConfirm={runProcessWithdrawals}
                    close={() => setState(s => ({ ...s, confirmingProcess: false, pinError: null }))}
                />
            )}
        </Box>
    )
}

// ── Main component ────────────────────────────────────────────────────────

const AdminWithdrawals: FC = () => {
    const [tab, setTab] = useState(0)
    const [state, setState] = useState<StateProps>({
        withdrawals: [],
        loading: true,
        refreshing: false,
        status: 'pending_processing',
        method: 'all',
        page: 0,
        pageSize: 25,
        count: 0
    })

    const { withdrawals, loading, refreshing, status, method, page, pageSize, count } = state

    const fetchWithdrawals = ({ newStatus, newMethod, newPage, newPageSize, processVariable }: any) => {
        const Status = newStatus || state.status
        const Method = newMethod || state.method
        const Page = newPage != null && newPage > -1 ? newPage : state.page
        const PageSize = newPageSize || state.pageSize
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const afterLoad = { loading: false, refreshing: false, status: Status, method: Method, page: Page, pageSize: PageSize }
        Apis({ module: 'Banks', action: 'getWithdrawals', status: statusParam(Status), method: Method === 'all' ? undefined : Method, page: Page, pageSize: PageSize })
            .then((resp: any) => setState(s => ({ ...s, withdrawals: resp.withdrawals || [], count: resp.count ?? s.count, ...afterLoad })))
            .catch(() => setState(s => ({ ...s, ...afterLoad })))
    }

    useEffect(() => {
        if (tab === 0) fetchWithdrawals({ processVariable: 'loading' })
        // eslint-disable-next-line
    }, [tab])

    return (
        <Box sx={{ pr: 5, height: '100%' }}>

            <PageTitle title="Withdrawals" />

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: 13 } }}>
                <Tab label="Property Payouts" />
                <Tab label="Referral Payouts" />
            </Tabs>

            {tab === 1 ? <ReferralPayouts /> : (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                            {count > 0 && (
                                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                                    {count.toLocaleString()} total
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 190 }}>
                                <Select
                                    value={status}
                                    onChange={(e: SelectChangeEvent) => fetchWithdrawals({ newStatus: e.target.value as StatusFilter, newPage: 0, processVariable: 'loading' })}
                                    sx={{ fontSize: 13, borderRadius: 2 }}
                                >
                                    <MenuItem sx={{ fontSize: 13 }} value="pending_processing">Pending &amp; Processing</MenuItem>
                                    <MenuItem sx={{ fontSize: 13 }} value="Pending">Pending</MenuItem>
                                    <MenuItem sx={{ fontSize: 13 }} value="Processing">Processing</MenuItem>
                                    <MenuItem sx={{ fontSize: 13 }} value="Completed">Completed</MenuItem>
                                    <MenuItem sx={{ fontSize: 13 }} value="all">All Statuses</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select
                                    value={method}
                                    onChange={(e: SelectChangeEvent) => fetchWithdrawals({ newMethod: e.target.value as MethodFilter, newPage: 0, processVariable: 'loading' })}
                                    sx={{ fontSize: 13, borderRadius: 2 }}
                                >
                                    <MenuItem sx={{ fontSize: 13 }} value="all">All Methods</MenuItem>
                                    <MenuItem sx={{ fontSize: 13 }} value="bank">Bank</MenuItem>
                                    <MenuItem sx={{ fontSize: 13 }} value="mpesa">Mpesa</MenuItem>
                                </Select>
                            </FormControl>
                            <Tooltip title="Refresh">
                                <IconButton size="small" onClick={() => fetchWithdrawals({ processVariable: 'refreshing' })} disabled={refreshing}>
                                    {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {!withdrawals.length || loading ?
                        <NoData message="No withdrawals found." loading={loading} /> :
                        <Paper sx={{ width: '100%' }} elevation={0} variant="outlined">
                            <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}></TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Recipient</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Method</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Purpose</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Amount</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Date</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {withdrawals.map(w => {
                                            const chipStyle = STATUS_STYLE[w.status] || STATUS_STYLE.Pending
                                            return (
                                                <TableRow hover key={w._id}>
                                                    <TableCell>
                                                        <Avatar src={w.recipientLogo?.small?.url} sx={{ width: 28, height: 28, fontSize: 12 }} />
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 13 }}>
                                                        <Typography sx={{ fontSize: 13 }}>{w.recipientName?.trim() || '—'}</Typography>
                                                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{w.phone || w.bankAccount || '—'}</Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 13 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                            {w.method === 'bank'
                                                                ? <AccountBalanceOutlined sx={{ fontSize: 17, color: '#1565c0' }} />
                                                                : <PhoneIphoneOutlined sx={{ fontSize: 17, color: '#00838f' }} />}
                                                            {w.method === 'bank' ? 'Bank' : 'Mpesa'}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 13 }}>{w.purpose || '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 13 }}>Ksh {numberWithCommas(w.amount || 0)}</TableCell>
                                                    <TableCell sx={{ fontSize: 13 }}>
                                                        {w.initiatedAt
                                                            ? new Date(w.initiatedAt).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' })
                                                            : '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={w.status}
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{
                                                                fontSize: 11,
                                                                color: chipStyle.color,
                                                                borderColor: chipStyle.border,
                                                                height: 20,
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={count}
                                page={page}
                                onPageChange={(_, newPage) => fetchWithdrawals({ newPage, processVariable: 'loading' })}
                                rowsPerPage={pageSize}
                                onRowsPerPageChange={e => fetchWithdrawals({ newPage: 0, newPageSize: parseInt(e.target.value, 10), processVariable: 'loading' })}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        </Paper>
                    }
                </>
            )}
        </Box>
    )
}

export default AdminWithdrawals
